import { useState } from 'react'

import {
  FinancialWorkspaceUpdateSettingDocument,
  GetFinancialWorkspaceSettingsDocument,
} from 'generated/resources/graphql'

import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  FinancialWorkspaceSettingsFormValues,
  LABOR_COST_CONCEPT_CALCULATION_TYPES,
} from './constants'

const getConceptIdsByCalculationType = (
  laborCostConcepts: Array<{ conceptId: number; laborCostCalculationType: string }> | undefined,
  calculationType: string
) => {
  return (
    laborCostConcepts
      ?.filter((concept) => concept.laborCostCalculationType === calculationType)
      .map((concept) => Number(concept.conceptId)) || []
  )
}

export const useFinancialWorkspaceSettings = () => {
  const [expensesConceptDetected, setExpensesConceptDetected] = useState<boolean>(false)

  const {
    data: financialWorkspaceSettings,
    loading: financialWorkspaceSettingsLoading,
    refetch: refetchFinancialWorkspaceSettings,
  } = useQuery(GetFinancialWorkspaceSettingsDocument)

  const currentFinancialWorkspaceSettings =
    financialWorkspaceSettings?.financialWorkspace?.settingsConnection?.edges[0]?.node

  const [updateFinancialWorkspaceSetting, { loading: updateLoading }] = useMutation(
    FinancialWorkspaceUpdateSettingDocument,
    { extractorFn: (data) => data.financialWorkspaceMutations.updateFinancialworkspaceSetting }
  )

  const defaultConceptIdsToAdd = getConceptIdsByCalculationType(
    currentFinancialWorkspaceSettings?.laborCostConcepts,
    LABOR_COST_CONCEPT_CALCULATION_TYPES.ADD_TO_TOTAL
  )

  const defaultConceptIdsToSubtract = getConceptIdsByCalculationType(
    currentFinancialWorkspaceSettings?.laborCostConcepts,
    LABOR_COST_CONCEPT_CALCULATION_TYPES.SUBTRACT_FROM_TOTAL
  )

  const { values, setFieldValue, dirty, handleSubmit, resetForm } =
    useForm<FinancialWorkspaceSettingsFormValues>({
      defaultValues: {
        conceptIdsToAdd: defaultConceptIdsToAdd,
        conceptIdsToSubtract: defaultConceptIdsToSubtract,
      },
      async onSubmit(data, { setErrors }) {
        const toAddConcepts =
          data.conceptIdsToAdd.length > 0 ? data.conceptIdsToAdd : defaultConceptIdsToAdd
        const laborCostConceptsToAdd = toAddConcepts.map((conceptId) => ({
          conceptId,
          laborCostCalculationType: LABOR_COST_CONCEPT_CALCULATION_TYPES.ADD_TO_TOTAL,
        }))

        const toSubtractConcepts =
          data.conceptIdsToSubtract.length > 0
            ? data.conceptIdsToSubtract
            : defaultConceptIdsToSubtract
        const laborCostConceptsToSubtract = toSubtractConcepts.map((conceptId) => ({
          conceptId,
          laborCostCalculationType: LABOR_COST_CONCEPT_CALCULATION_TYPES.SUBTRACT_FROM_TOTAL,
        }))

        const result = await updateFinancialWorkspaceSetting({
          variables: {
            id: Number(currentFinancialWorkspaceSettings?.id),
            laborCostConcepts: [...laborCostConceptsToAdd, ...laborCostConceptsToSubtract],
          },
        })

        const handled = handleResult(result, (resultErrors) => {
          setErrors(resultErrors)
        })

        if (handled.success) {
          refetchFinancialWorkspaceSettings()
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })

          resetForm({ values })
        }

        return handled
      },
    })

  const showBanner = expensesConceptDetected && dirty
  const loading = updateLoading || financialWorkspaceSettingsLoading

  return {
    values,
    setFieldValue,
    dirty,
    handleSubmit,
    loading,
    showBanner,
    defaultConceptIdsToAdd,
    defaultConceptIdsToSubtract,
    expensesConceptDetected,
    setExpensesConceptDetected,
  }
}
